/* =========================================================================
 lrc-ajax-custom-cart-drawer.js, cart drawer javascript.
   LRC Cart Drawer (FULL) — sections-less AJAX + rewards sync (qty/price)
   Expects in drawer HTML (Liquid):
   <aside data-lrc-cart-panel data-section-id="{{ section.id }}">
     <div data-lrc-cart-root> ... may contain #lrc-rewards-root ... </div>
   </aside>
   #lrc-rewards-root carries:
     data-lrc-mode="subs|one"
     data-lrc-qty="<pass count for subs>"
     data-lrc-one-cents="<one-time subtotal in cents>"
     data-lrc-rewards="<escaped JSON array>"
   ======================================================================== */

/* ---------------- Local cart cache (for faster getCart) ---------------- */
(function () {
    const LRC_CART_CACHE_KEY = 'lrc-custom-get-cart';

    function lrcWriteCartCache(cart) {
        try {
            localStorage.setItem(LRC_CART_CACHE_KEY, JSON.stringify({ t: Date.now(), cart }));
        } catch { /* no-op */ }
    }

    function lrcReadCartCache() {
        try {
            const raw = localStorage.getItem(LRC_CART_CACHE_KEY);
            if (!raw) return null;
            const obj = JSON.parse(raw);
            return obj && obj.cart ? obj.cart : null;
        } catch { return null; }
    }

    async function lrcFetchCartFresh() {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
        if (!res.ok) throw new Error('cart fetch failed');
        const cart = await res.json();
        lrcWriteCartCache(cart);
        return cart;
    }

    async function lrcGetCartCachedOrFetch(force = false) {
        if (!force) {
            const cached = lrcReadCartCache();
            if (cached) return cached;
        }
        return lrcFetchCartFresh();
    }

    // expose globally
    window.lrcWriteCartCache = lrcWriteCartCache;
    window.lrcReadCartCache = lrcReadCartCache;
    window.lrcFetchCartFresh = lrcFetchCartFresh;
    window.lrcGetCartCachedOrFetch = lrcGetCartCachedOrFetch;
})();

/* ---------------- Header cart count helpers ---------------- */
window.lrcSetHeaderCartCount = function setHeader(n) {
    document.querySelectorAll('.lrc_header_cart_icon, [data-cart-count]').forEach(el => {
        el.textContent = n;
        el.classList.add('cart-count-bump');
        setTimeout(() => el.classList.remove('cart-count-bump'), 300);
    });
};
window.lrcRefreshHeaderCartCount = async function refreshHeader() {
    try {
        const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
        window.lrcWriteCartCache?.(cart); // cache it
        window.lrcSetHeaderCartCount(cart?.item_count ?? 0);
    } catch (e) { console.warn('Header count refresh failed', e); }
};

/* ---------------- Rewards sync (read attrs → call widget) ---------------- */
function lrcSyncRewardsFromAttrs(opts = { animate: 'forward' }) {
    const root = document.getElementById('lrc-rewards-root');
    const inst = window.LRC_Rewards;
    if (!root || !inst) return;

    const mode = (root.getAttribute('data-lrc-mode') || '').trim();
    const pass = Number(root.getAttribute('data-lrc-qty')) || 0;
    const cents = Number(root.getAttribute('data-lrc-one-cents')) || 0;

    try {
        if (mode === 'one' && typeof inst.setPrice === 'function') {
            inst.setPrice(cents, opts);
        } else if (mode === 'subs' && typeof inst.setQty === 'function') {
            inst.setQty(pass, opts);
        }
    } catch (e) {
        console.warn('[LRC] rewards sync failed', e);
    }
}

/* ---------------- Global helper: prune add-ons already in cart ---------------- */
window.lrcPruneAddonsAlreadyInCart = function lrcPruneAddonsAlreadyInCart(cart) {
    try {
        const idsInCart = new Set(
            (cart?.items || [])
                .map(li => Number(li.variant_id))
                .filter(Number.isFinite)
        );

        document.querySelectorAll('[data-add-variant]').forEach(btn => {
            const variantId = Number(btn.getAttribute('data-add-variant'));
            if (!Number.isFinite(variantId)) return;

            if (idsInCart.has(variantId)) {
                const cardWrapper = btn.parentElement;
                cardWrapper?.remove();
            }
        });
    } catch { /* no-op */ }
};


/* ---------------- Drawer updater (sections-less) ---------------- */
// Preserves/reuses the rewards node and refreshes its attributes.
window.lrcUpdateCartDrawer = async function lrcUpdateCartDrawer(sectionsFromResponse, opts = {}) {
    const { open = true, syncRewards = true, refreshHeader = true } = opts;
    const panel = document.querySelector('[data-lrc-cart-panel]');
    const sectionId = panel?.getAttribute('data-section-id');
    const rootSel = '[data-lrc-cart-root]';
    if (!panel || !sectionId) { await window.lrcRefreshHeaderCartCount?.(); return false; }

    const ensureOpen = () => {
        if (window.lrcCartDrawer?.open) return window.lrcCartDrawer.open();
        const overlay = document.querySelector('[data-lrc-cart-overlay]');
        panel.style.transform = 'translateX(0)';
        panel.setAttribute('aria-hidden', 'false');
        if (overlay) { overlay.style.opacity = '1'; overlay.style.pointerEvents = 'auto'; }
        document.documentElement.classList.add('cart-drawer-open');
    };

    const copyRewardsAttrs = (fromEl, toEl) => {
        ['data-lrc-mode', 'data-lrc-qty', 'data-lrc-one-cents', 'data-lrc-rewards', 'data-lrc-one-subtotal', 'data-lrc-subqty', 'data-lrc-one-thresholds', 'data-lrc-one-config'].forEach(attr => {
            const v = fromEl.getAttribute(attr);
            if (v !== null) toEl.setAttribute(attr, v); else toEl.removeAttribute(attr);
        });
    };

    const nukeRewardsInstance = () => {
        try { window.LRC_Rewards?.destroy?.(); } catch { }
        window.LRC_Rewards = null;
        window.__lrcRewardsPass = 0;
    };

    const swapFromHtml = (html) => {
        const temp = document.createElement('div');
        temp.innerHTML = html.trim();

        const nextRoot = temp.querySelector(rootSel);
        const curRoot = panel.querySelector(rootSel);
        if (!nextRoot || !curRoot) return false;

        const curRewards = curRoot.querySelector('#lrc-rewards-root');
        const nextRewards = nextRoot.querySelector('#lrc-rewards-root');

        if (curRewards) {
            if (nextRewards) {
                copyRewardsAttrs(nextRewards, curRewards);
                nextRewards.replaceWith(curRewards);
            } else {
                curRewards.remove();
                nukeRewardsInstance();
            }
        }
        curRoot.replaceWith(nextRoot);
        return true;
    };

    // 1) If we were given sections HTML, try that first
    let swapped = false;
    if (sectionsFromResponse && sectionsFromResponse[sectionId]) {
        swapped = swapFromHtml(sectionsFromResponse[sectionId]);
    }

    // 2) Fallback: fetch section DOM
    if (!swapped) {
        try {
            const url = `${location.pathname}?section_id=${encodeURIComponent(sectionId)}`;
            const html = await fetch(url, { headers: { 'Accept': 'text/html', 'Cache-Control': 'no-cache' } }).then(r => r.text());
            swapped = swapFromHtml(html);
        } catch (e) { console.warn('[LRC] drawer section fetch failed:', e); }
    }

    // Open, refresh header, notify, and sync rewards animation
    if (open) ensureOpen();
    if (refreshHeader) {
        try { await window.lrcRefreshHeaderCartCount?.(); } catch { }
    }

    // >>> HARD RESET LS IF STREAMS ARE EMPTY (read from fresh HTML)
    try {
        const r = document.getElementById('lrc-rewards-root');
        if (r) {
            const subQty = parseInt(r.getAttribute('data-lrc-subqty') || '0', 10) || 0;
            const oneSubtotal = parseInt(r.getAttribute('data-lrc-one-subtotal') || '0', 10) || 0;
            if (subQty === 0) localStorage.setItem('lrc_rewards_subs', JSON.stringify({ maxCelebrated: 0, lastSeen: 0 }));
            if (oneSubtotal === 0) localStorage.setItem('lrc_rewards_one', JSON.stringify({ maxCelebrated: 0, lastSeen: 0 }));
        }
    } catch { /* no-op */ }

    // >>> PRUNE add-ons + UPDATE LOCAL CART CACHE
    try {
        // use cached-or-fetch for speed, then be sure cache stays current
        const cart = await window.lrcGetCartCachedOrFetch?.() || await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
        window.lrcWriteCartCache?.(cart);
        window.lrcPruneAddonsAlreadyInCart(cart);
    } catch { /* no-op */ }

    document.dispatchEvent(new CustomEvent('lrc:drawer-updated'));

    // mountRewards() already handles the full rebuild and pass animation via lrc:drawer-updated above.
    // lrcSyncRewardsFromAttrs is intentionally suppressed here to prevent a double animation pass
    // that causes the rewards slider to flash/rebuild twice on mobile.

    return swapped;
};

/* ---------------- Drawer behavior (events & API) ---------------- */
(() => {
    'use strict';
    const $ = (sel, root = document) => root.querySelector(sel);
    const panel = $('[data-lrc-cart-panel]');
    if (!panel) return;

    // ---- CONFIG: how gifts are marked (must match your other scripts) ----
    const GIFT_TAG = '_lrcGift';
    const isNonGift = (li) =>
        ((li.product_type || '').toLowerCase() !== 'gift') &&
        (li.properties?.[GIFT_TAG] !== '1');

    function okOrThrow(res) {
        if (!res.ok) return res.json().catch(() => ({})).then(err => { throw err; });
        return res.json();
    }
    const API = {
        add(body) {
            const url = '/cart/add.js';
            const opts = { method: 'POST', headers: { 'Accept': 'application/json' } };
            if (body instanceof FormData) { opts.body = body; }
            else { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
            return fetch(url, opts).then(okOrThrow);
        },
        change({ id, quantity }) {
            return fetch('/cart/change.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ id, quantity })
            }).then(okOrThrow);
        },
        update(payload) {
            return fetch('/cart/update.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify(payload)
            }).then(okOrThrow);
        },
        get() { return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(okOrThrow); },
        clear() {
            return fetch('/cart/clear.js', { method: 'POST', headers: { 'Accept': 'application/json' } }).then(okOrThrow);
        }
    };

    // Figure out if the clicked line is the ONLY remaining non-gift unit (subs or one-time).
    async function isLastNonGiftUnit(targetKey) {
        try {
            const cart = await (window.lrcGetCartCachedOrFetch?.() || API.get());
            const nonGifts = (cart.items || []).filter(isNonGift);
            if (!nonGifts.length) return false;

            const target = nonGifts.find(li => li.key === targetKey);
            if (!target || Number(target.quantity) <= 0) return false;

            const totalUnits = nonGifts.reduce((sum, li) => sum + (Number(li.quantity) || 0), 0);
            return totalUnits === 1 && Number(target.quantity) === 1;
        } catch { return false; }
    }

    function ensureDrawerOpen() {
        if (window.lrcCartDrawer?.open) return window.lrcCartDrawer.open();
        const overlay = $('[data-lrc-cart-overlay]');
        panel.style.transform = 'translateX(0)';
        panel.setAttribute('aria-hidden', 'false');
        if (overlay) { overlay.style.opacity = '1'; overlay.style.pointerEvents = 'auto'; }
        document.documentElement.classList.add('cart-drawer-open');
    }
    function setBusy(state) {
        panel.classList.remove('opacity-50');
        panel.style.pointerEvents = state ? 'none' : '';
    }

    /* ---- Intercept product forms (Add to Cart) ---- */
    document.addEventListener('submit', async (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (!form || form.hasAttribute('data-no-ajax')) return;

        e.preventDefault(); e.stopPropagation();
        const fd = new FormData(form);
        setBusy(true);
        try {
            await API.add(fd);
            // ensure cache reflects server state ASAP
            try {
                const cart = await API.get();
                window.lrcWriteCartCache?.(cart);
            } catch { }
            await window.lrcUpdateCartDrawer();          // swaps HTML and syncs rewards (qty/price) and caches again
            ensureDrawerOpen();
        } catch (err) {
            console.error('[Cart add]', err);
            alert(err?.description || 'Could not add to cart.');
        } finally { setBusy(false); }
    });

    /* ---- Quantity +/- in drawer ---- */
    panel.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-change-qty]');
        if (!btn) return;

        e.preventDefault(); e.stopPropagation();
        const group = btn.closest('[data-qty-group]') || btn.parentElement || panel;
        const qtyEl = group.querySelector('[data-qty-value], .lrc-qty, span');
        const spinner = group.querySelector('[data-spinner]');
        const controls = group.querySelectorAll('[data-change-qty]');

        const key = btn.getAttribute('data-line-key');
        const delta = parseInt(btn.getAttribute('data-change-qty'), 10) || 0;
        const cur = qtyEl ? parseInt((qtyEl.textContent || qtyEl.value || '1'), 10) || 1 : 1;
        const next = Math.max(0, cur + delta);

        // If user is decrementing this line to 0, and it's the last non-gift unit, clear entire cart.
        if (delta < 0 && next === 0 && (await isLastNonGiftUnit(key))) {
            spinner?.classList.remove('hidden');
            controls.forEach(b => (b.disabled = true));
            group.setAttribute('aria-busy', 'true');
            try {
                await API.clear();                 // nukes everything, including gifts
                try {
                    const cart = await API.get();
                    window.lrcWriteCartCache?.(cart);
                } catch { }
                await window.lrcUpdateCartDrawer();
                ensureDrawerOpen();
            } catch (err) {
                console.error('[Cart clear on last unit]', err);
                alert(err?.description || 'Could not update quantity.');
            } finally {
                spinner?.classList.add('hidden');
                controls.forEach(b => (b.disabled = false));
                group.removeAttribute('aria-busy');
            }
            return; // stop normal per-line flow
        }

        // Normal per-line change
        spinner?.classList.remove('hidden');
        controls.forEach(b => (b.disabled = true));
        group.setAttribute('aria-busy', 'true');

        try {
            const result = await API.change({ id: key, quantity: next });
            // change.js returns full cart → cache immediately
            if (result && result.items) window.lrcWriteCartCache?.(result);
            await window.lrcUpdateCartDrawer();          // will also animate rewards and cache again
            ensureDrawerOpen();
        } catch (err) {
            console.error('[Cart change]', err);
            alert(err?.description || 'Could not update quantity.');
        } finally {
            spinner?.classList.add('hidden');
            controls.forEach(b => (b.disabled = false));
            group.removeAttribute('aria-busy');
        }
    });

    /* ---- Remove item ---- */
    panel.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-remove]');
        if (!btn) return;

        e.preventDefault(); e.stopPropagation();
        const key = btn.getAttribute('data-line-key');

        // If removing the last remaining non-gift unit, clear the cart instead.
        if (await isLastNonGiftUnit(key)) {
            setBusy(true);
            try {
                const result = await API.clear();
                if (result && result.items) window.lrcWriteCartCache?.(result);
                await window.lrcUpdateCartDrawer();
                ensureDrawerOpen();
            } catch (err) {
                console.error('[Cart clear on last remove]', err);
                alert(err?.description || 'Could not remove item.');
            } finally { setBusy(false); }
            return;
        }

        // Normal single-line remove
        setBusy(true);
        try {
            const result = await API.change({ id: key, quantity: 0 });
            if (result && result.items) window.lrcWriteCartCache?.(result);
            await window.lrcUpdateCartDrawer();
            ensureDrawerOpen();
        } catch (err) {
            console.error('[Cart remove]', err);
            alert(err?.description || 'Could not remove item.');
        } finally { setBusy(false); }
    });

    /* ---- Add-ons quick add ---- */
    panel.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-add-variant]');
        if (!btn) return;

        e.preventDefault(); e.stopPropagation();
        const id = parseInt(btn.getAttribute('data-add-variant'), 10);
        const qty = parseInt(btn.getAttribute('data-add-qty'), 10) || 1;
        if (!id) return;

        const cardWrapper = btn.parentElement;

        const icon = btn.querySelector('[data-add-icon]');
        const spinner = btn.querySelector('[data-spinner]');

        btn.disabled = true;
        icon?.classList.add('invisible');
        spinner?.classList.remove('hidden');
        cardWrapper?.setAttribute('aria-busy', 'true');

        try {
            await API.add({ items: [{ id, quantity: qty }] });
            // Optimistic remove from grid
            cardWrapper?.remove();

            // Refresh cache quickly
            try {
                const cart = await API.get();
                window.lrcWriteCartCache?.(cart);
            } catch { }

            // Refresh drawer (rewards, totals, etc.)
            await window.lrcUpdateCartDrawer();

            // Extra safety: prune any add-ons that match items in the cart post-refresh
            try {
                const cart = await API.get();
                window.lrcWriteCartCache?.(cart);
                window.lrcPruneAddonsAlreadyInCart(cart);
            } catch { }

            ensureDrawerOpen();
        } catch (err) {
            console.error('[Cart add-on]', err);
            alert(err?.description || 'Could not add item.');
        } finally {
            spinner?.classList.add('hidden');
            icon?.classList.remove('invisible');
            btn.disabled = false;
            cardWrapper?.removeAttribute('aria-busy');
        }
    });

    /* ---- External open trigger (optional) ---- */
    document.addEventListener('lrc:open-cart', ensureDrawerOpen);

    /* ---- When the drawer HTML is swapped, try syncing again (safety) ---- */
    document.addEventListener('lrc:drawer-updated', () => {
        requestAnimationFrame(() => lrcSyncRewardsFromAttrs({ animate: 'forward' }));
    });

    /* ---- Prune add-ons on initial load & BFCache restores ---- */
    async function bootPruneAddons() {
        try {
            const cart = await (window.lrcGetCartCachedOrFetch?.() || API.get());
            window.lrcWriteCartCache?.(cart);
            window.lrcPruneAddonsAlreadyInCart(cart);
        } catch { }
    }
    document.addEventListener('DOMContentLoaded', bootPruneAddons);
    window.addEventListener('pageshow', (ev) => {
        // Always safe & idempotent
        bootPruneAddons();
    });
})();

// ======== Subscription Frequency Selector (per-line) — PERSISTENT & REWARD-GROUP-AWARE ========
(() => {
    const $ = (s, r = document) => r.querySelector(s);
    const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
    const norm = s => String(s || '').trim().toLowerCase();

    /* ========= Storage ========= */
    const LS_FREQ = 'lrc-subs-frequency-pref';   // { [variantId]: token | 'one-time' }
    const LS_GROUP = 'lrc-subs-last-group';       // { [variantId]: lastGroupName } (for debug/visibility)

    const loadMap = (k) => { try { return JSON.parse(localStorage.getItem(k) || '{}'); } catch { return {}; } };
    const saveMap = (k, obj) => { try { localStorage.setItem(k, JSON.stringify(obj)); } catch { } };

    const getPrefToken = (variantId) => loadMap(LS_FREQ)[String(variantId)] || '';
    const setPrefToken = (variantId, token) => { const m = loadMap(LS_FREQ); m[String(variantId)] = token || ''; saveMap(LS_FREQ, m); };

    const getLastGroup = (variantId) => loadMap(LS_GROUP)[String(variantId)] || '';
    const setLastGroup = (variantId, name) => { const m = loadMap(LS_GROUP); m[String(variantId)] = name || ''; saveMap(LS_GROUP, m); };

    /* ========= Rewards: unlocked group name ========= */
    function getUnlockedSubsPlanName() {
        const root = $('#lrc-rewards-root');
        if (!root) return null;
        if ((root.getAttribute('data-lrc-mode') || '').trim() !== 'subs') return null;
        const pass = Number(root.getAttribute('data-lrc-qty') || 0) || 0;
        try {
            const ta = document.createElement('textarea');
            ta.innerHTML = root.getAttribute('data-lrc-rewards') || '[]';
            const rewards = JSON.parse(ta.value || '[]');
            const unlocked = rewards.find(r => Number(r.id) === pass);
            return unlocked?.subs_selling_plan_name ? String(unlocked.subs_selling_plan_name).trim() : null;
        } catch { return null; }
    }

    /* ========= Cart helpers ========= */
    async function getCart() {
        try { return await (window.lrcGetCartCachedOrFetch?.() || fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json())); }
        catch { return null; }
    }
    async function refreshDrawer() {
        try {
            const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
            window.lrcWriteCartCache?.(cart);
        } catch { }
        await window.lrcUpdateCartDrawer?.();
    }

    /* ========= Plans parsing & labeling ========= */
    function parseGroups(rawAttr) {
        if (!rawAttr) return [];
        try {
            const ta = document.createElement('textarea'); ta.innerHTML = rawAttr;
            const arr = JSON.parse(ta.value || '[]');
            return Array.isArray(arr) ? arr : [];
        } catch { return []; }
    }

    // Canonical label & token helpers
    function canonicalLabel(n, unit) {
        const u = String(unit).toLowerCase();
        if (u.startsWith('day')) return `${n} ${n === 1 ? 'Day' : 'Days'}`;
        if (u.startsWith('week')) return `${n} ${n === 1 ? 'Week' : 'Weeks'}`;
        if (u.startsWith('month')) return `${n} ${n === 1 ? 'Month' : 'Months'}`;
        return `${n} ${unit}`;
    }
    function labelToToken(label) {
        const m = String(label || '').match(/(\d+)\s*(day|days|week|weeks|month|months)/i);
        if (!m) return norm(label);
        const n = Number(m[1]); const u = m[2].toLowerCase();
        return u.startsWith('day') ? `${n}d` : u.startsWith('week') ? `${n}w` : `${n}m`;
    }
    function planToToken(plan) {
        const unit = plan?.delivery_policy?.interval || plan?.billing_policy?.interval || '';
        const count = plan?.delivery_policy?.interval_count || plan?.billing_policy?.interval_count || '';
        if (unit && count) return labelToToken(canonicalLabel(Number(count), String(unit)));
        const name = String(plan?.name || '');
        const opt = Array.isArray(plan?.options) ? (plan.options[0]?.value || plan.options[0]?.name || '') : '';
        const txt = [name, opt].find(v => /(\d+)\s*(day|days|week|weeks|month|months)/i.test(String(v))) || name;
        return labelToToken(txt);
    }
    function tokenToDays(token) {
        const m = String(token || '').match(/^(\d+)([dwm])$/i);
        if (!m) return NaN;
        const n = Number(m[1]); const u = m[2].toLowerCase();
        return u === 'd' ? n : u === 'w' ? n * 7 : n * 30; // months≈30
    }
    function findPlanByToken(plans, wantedToken) {
        if (!wantedToken) return null;
        const exact = plans.find(p => planToToken(p) === wantedToken);
        if (exact) return exact;
        const wantDays = tokenToDays(wantedToken);
        if (isNaN(wantDays)) return null;
        let best = null, bestDiff = Infinity;
        for (const p of plans) {
            const d = tokenToDays(planToToken(p));
            if (!isNaN(d)) {
                const diff = Math.abs(d - wantDays);
                if (diff < bestDiff) { bestDiff = diff; best = p; }
            }
        }
        return best;
    }

    /* ========= UI ========= */
    function ensureUI(card) {
        let wrap = card.querySelector('[data-subs-frequency-ui]');
        if (!wrap) {
            wrap = document.createElement('div');
            wrap.setAttribute('data-subs-frequency-ui', '');
            wrap.className = 'mt-2';
            wrap.innerHTML = `
        <label class="block text-[11px] text-neutral-600 mb-1">Delivery frequency</label>
        <select class="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-subs-frequency-select aria-label="Change delivery frequency">
          <option disabled>Loading…</option>
        </select>
        <p class="mt-1 text-[11px] text-neutral-500" data-subs-frequency-hint style="display:none;"></p>
      `;
            (card.querySelector('.col-span-2') || card).appendChild(wrap);
        }
        return wrap;
    }

    function renderOptions(selectEl, plans) {
        const opts = [{ value: 'one-time', label: 'One-Time (no subscription)', token: 'one-time' }];
        for (const p of plans) {
            opts.push({ value: String(p.id), label: readableLabel(p), token: planToToken(p) });
        }
        selectEl.innerHTML = opts.map(o => `<option value="${o.value}" data-token="${o.token}">${o.label}</option>`).join('');
    }

    function readableLabel(plan) {
        const unit = plan?.delivery_policy?.interval || plan?.billing_policy?.interval || '';
        const count = plan?.delivery_policy?.interval_count || plan?.billing_policy?.interval_count || '';
        if (unit && count) return canonicalLabel(Number(count), String(unit));
        const name = String(plan?.name || '').trim();
        const opt = Array.isArray(plan?.options) ? (plan.options[0]?.value || plan.options[0]?.name || '') : '';
        const txt = [name, opt].find(v => /(\d+)\s*(day|days|week|weeks|month|months)/i.test(String(v))) || name;
        if (txt) {
            const m = String(txt).match(/(\d+)\s*(day|days|week|weeks|month|months)/i);
            if (m) return canonicalLabel(Number(m[1] || 1), m[2]);
        }
        return name || `Plan ${plan?.id}`;
    }

    // Expose hydrate for external triggers (rewards events)
    let __hydrating = false;
    async function hydrateFrequencyUI() {
        if (__hydrating) return;
        __hydrating = true;

        const unlockedName = getUnlockedSubsPlanName();
        const cart = await getCart();

        $$('.lrc-subscription-item-card').forEach(card => {
            const wrap = ensureUI(card);
            const sel = wrap.querySelector('[data-subs-frequency-select]');
            const hint = wrap.querySelector('[data-subs-frequency-hint]');

            const groups = parseGroups(card.getAttribute('data-lrc-subs-selling-plan'));
            if (!groups.length) { sel.innerHTML = '<option disabled>No subscription plans</option>'; sel.disabled = true; return; }

            const vId = Number(card.getAttribute('data-lrc-variant-id'));
            const line = (cart?.items || []).find(li => Number(li.variant_id) === vId && li.selling_plan_allocation);
            const curPlanId = line?.selling_plan_allocation?.selling_plan?.id || null;
            const currentToken = curPlanId && line ? planToToken(line.selling_plan_allocation.selling_plan) : '';

            // PDP memory (consume once)
            const pdpMem = window.__lrcReadPdpPlanOnce?.(vId) || null;
            const pdpToken = pdpMem?.token || '';

            // Decide which group to show: unlocked > group containing current > first
            let group = unlockedName ? groups.find(g => norm(g?.name) === norm(unlockedName)) : null;
            if (!group && curPlanId) group = groups.find(g => (g?.selling_plans || []).some(p => String(p?.id) === String(curPlanId))) || null;
            if (!group) group = groups[0];

            const plans = Array.isArray(group?.selling_plans) ? group.selling_plans : [];
            if (!plans.length) { sel.innerHTML = '<option disabled>No frequencies in this plan</option>'; sel.disabled = true; return; }

            // Build dropdown
            renderOptions(sel, plans);

            // Stored preference
            const storedToken = getPrefToken(vId);

            // Selection policy: the cart line's actual selling_plan is the source
            // of truth. localStorage and PDP memory are useful only when there's
            // no existing cart line to read from. Previously, rewards-unlocked
            // mode let pdpToken/storedToken win — that caused the dropdown to
            // silently render a different cadence than the line's real plan.
            let wantToken = currentToken || pdpToken || storedToken || planToToken(plans[0]);

            // Map token → actual plan in THIS group
            let preValue = 'one-time';
            if (wantToken === 'one-time') {
                preValue = 'one-time';
            } else {
                const m = findPlanByToken(plans, wantToken) || plans[0];
                preValue = m ? String(m.id) : 'one-time';
            }
            sel.value = preValue;

            // Save for future runs
            setPrefToken(vId, (wantToken || (preValue === 'one-time' ? 'one-time' : planToToken(plans.find(p => String(p.id) === preValue) || plans[0]))));
            setLastGroup(vId, String(group?.name || ''));

            // Hint
            if (hint) {
                if (unlockedName && norm(group?.name) === norm(unlockedName)) {
                    hint.style.display = 'block';
                    hint.textContent = `Unlocked reward plan: ${group.name}`;
                } else { hint.style.display = 'none'; }
            }

            // Change handler (persist + API + pending map)
            sel.onchange = async () => {
                sel.disabled = true;
                try {
                    const chosenValue = sel.value; // 'one-time' or plan id
                    const chosenOpt = sel.selectedOptions[0];
                    const chosenToken = chosenOpt?.dataset?.token || (chosenValue === 'one-time' ? 'one-time' : '');

                    // Fresh line/key snapshot
                    const fresh = await getCart();
                    let freshLine = (fresh?.items || []).find(li => Number(li.variant_id) === vId && li.selling_plan_allocation);
                    if (!freshLine) freshLine = (fresh?.items || []).find(li => Number(li.variant_id) === vId);
                    const safeKey = freshLine?.key;
                    const safeQty = Number(freshLine?.quantity || 1);

                    if (safeKey) {
                        const payload = {
                            id: safeKey,
                            quantity: safeQty,
                            selling_plan: (chosenValue === 'one-time' ? null : Number(chosenValue))
                        };
                        const res = await fetch('/cart/change.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                            body: JSON.stringify(payload)
                        });
                        const json = await res.json();
                        try { window.lrcWriteCartCache?.(json); } catch { }
                    } else {
                        // Rare fallback: add new line with chosen plan
                        const payload = (chosenValue === 'one-time')
                            ? { items: [{ id: vId, quantity: safeQty }] }
                            : { items: [{ id: vId, quantity: safeQty, selling_plan: Number(chosenValue) }] };
                        const res = await fetch('/cart/add.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                            body: JSON.stringify(payload)
                        });
                        await res.json().catch(() => { });
                    }

                    // Persist frequency token preference
                    setPrefToken(vId, chosenToken);

                    // Mirror into your pending LS so checkout sync reads it
                    try {
                        const k = 'lrc-custom-pending-cart-ops';
                        const p = JSON.parse(localStorage.getItem(k) || '{}') || {};
                        p.planByVariantId = p.planByVariantId || {};
                        if (chosenValue === 'one-time') delete p.planByVariantId[String(vId)];
                        else p.planByVariantId[String(vId)] = Number(chosenValue);
                        localStorage.setItem(k, JSON.stringify(p));
                    } catch { }

                    // Refresh drawer UI
                    await refreshDrawer();
                } catch (e) {
                    console.warn('[LRC] frequency change failed', e);
                    alert('Could not update subscription frequency. Please try again.');
                } finally {
                    sel.disabled = false;
                }
            };
        });

        __hydrating = false;
    }

    // expose so other scripts can force rehydrate
    window.lrcHydrateFrequencyUI = hydrateFrequencyUI;

    // Initial + drawer updates
    document.addEventListener('DOMContentLoaded', hydrateFrequencyUI);
    document.addEventListener('lrc:drawer-updated', hydrateFrequencyUI);

    // Reward system hooks → on unlock/lock, rebuild all dropdowns using stored tokens
    document.addEventListener('lrc:reward-unlocked', () => { window.lrcHydrateFrequencyUI?.(); });
    document.addEventListener('lrc:reward-locked', () => { window.lrcHydrateFrequencyUI?.(); });
})();

/* ===== Convert One-Time → Subscription (frequency-aware, sticky by name) ===== */
(() => {
    'use strict';

    const $ = (s, r = document) => r.querySelector(s);
    const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
    const norm = s => String(s || '').trim().toLowerCase();

    /* ---------- Money helpers ---------- */
    function formatMoneyFromCents(c) {
        const n = Math.max(0, Math.round(Number(c) || 0));
        if (window.Shopify?.formatMoney) { try { return Shopify.formatMoney(n, window.theme?.moneyFormat || '${{amount}}'); } catch { } }
        return (n / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify?.currency?.active || 'USD') });
    }
    function computeSaveCentsFromAdjustmentLocal(adj, unitPriceCents) {
        if (!adj || unitPriceCents <= 0) return 0;
        const t = String(adj.value_type || '').toLowerCase();
        const v = Number(adj.value || 0);
        if (t === 'percentage') return Math.max(0, Math.round(unitPriceCents * (v / 100)));
        if (t === 'fixed_amount') {
            const cents = v > 1000 ? Math.round(v) : Math.round(v * 100);
            return Math.max(0, Math.min(unitPriceCents, cents));
        }
        return 0;
    }

    /* ---------- Parse rewards (from your hidden synced block) ---------- */
    function getOneTimeRewardsConfig() {
        const wrap = document.querySelector('.synced-subs-rewards-data');
        if (!wrap) return { mode: 'amount-based', tiers: [] };
        const mode = (wrap.getAttribute('data-lrc-subs-mode') || '').trim() || 'amount-based';
        const tiers = [];
        wrap.querySelectorAll('.synced-subs-reward-card').forEach(el => {
            const th = Number(el.getAttribute('data-lrc-subs-thresholds') || 0);
            const name = (el.getAttribute('data-lrc-plan-name') || '').trim();
            if (th > 0 && name) tiers.push({ threshold: th, planName: name });
        });
        // sort ascending by threshold
        tiers.sort((a, b) => a.threshold - b.threshold);
        return { mode, tiers };
    }

    /* ---------- Baseline from current SUBSCRIPTION lines ---------- */
    async function getCart() {
        try {
            return await (window.lrcGetCartCachedOrFetch?.() ||
                fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json()));
        } catch { return null; }
    }
    function isGiftLine(li) {
        return (String(li.product_type || '').toLowerCase() === 'gift') ||
            (li.properties && li.properties._lrcGift === '1');
    }
    function computeSubsBaseline(cart, mode) {
        if (!cart || !Array.isArray(cart.items)) return 0;
        const subs = cart.items.filter(li => !isGiftLine(li) && !!li.selling_plan_allocation);
        if (mode === 'quantity-based') {
            return subs.reduce((t, li) => t + (Number(li.quantity) || 0), 0);
        }
        // amount-based → subtotal cents of subs
        return subs.reduce((t, li) => t + (Number(li.price || 0) * Number(li.quantity || 0)), 0);
    }

    /* ---------- Get unlocked tier given metric ---------- */
    function findUnlockedTier(tiers, metric) {
        if (!tiers.length) return null;
        let unlocked = null;
        for (const t of tiers) {
            if (metric >= t.threshold) unlocked = t; else break;
        }
        return unlocked;
    }

    /* ---------- Plans parsing / matching ---------- */
    function parseGroups(rawAttr) {
        if (!rawAttr) return [];
        try {
            const ta = document.createElement('textarea');
            ta.innerHTML = rawAttr; // unescape
            const arr = JSON.parse(ta.value || '[]');
            return Array.isArray(arr) ? arr : [];
        } catch { return []; }
    }
    function findGroupByExactName(groups, planName) {
        const tgt = norm(planName);
        return groups.find(g => norm(g?.name) === tgt) || null;
    }

    // Choose "the" plan inside a group to compute discount.
    // If multiple, prefer one with highest discount (percentage or fixed).
    function pickBestPlanForSavings(group, unitPriceCents) {
        const plans = Array.isArray(group?.selling_plans) ? group.selling_plans : [];
        if (!plans.length) return null;
        let best = null, bestSave = -1;
        for (const p of plans) {
            const adj = Array.isArray(p.price_adjustments) ? p.price_adjustments[0] : null;
            const save = computeSaveCentsFromAdjustmentLocal(adj, unitPriceCents);
            if (save > bestSave) { bestSave = save; best = p; }
        }
        return best;
    }

    /* ---------- Compute savings for a one-time line ---------- */
    function computeLineSavingsCents(card, unlockedPlanName, mode, baseline, cart) {
        // line inputs
        const qty = Number(card.getAttribute('data-qty') || 1);
        const unitCents = Number(card.getAttribute('data-unit-price-cents') || 0);
        const groupsRaw = card.getAttribute('data-selling-plan-groups');

        // If no groups, cannot compute subs price
        const groups = parseGroups(groupsRaw);
        if (!groups.length || !unlockedPlanName) return { saveCents: 0, planId: null, subsLineCents: null };

        // find group with plan name
        const group = findGroupByExactName(groups, unlockedPlanName) || groups[0];
        if (!group) return { saveCents: 0, planId: null, subsLineCents: null };

        const chosen = pickBestPlanForSavings(group, unitCents);
        if (!chosen) return { saveCents: 0, planId: null, subsLineCents: null };

        const adj = Array.isArray(chosen.price_adjustments) ? chosen.price_adjustments[0] : null;
        const perUnitSave = computeSaveCentsFromAdjustmentLocal(adj, unitCents);
        const saveCents = Math.max(0, perUnitSave * qty);
        const subsPerUnit = Math.max(0, unitCents - perUnitSave);
        const subsLineCents = subsPerUnit * qty;

        return { saveCents, planId: chosen.id, subsLineCents };
    }

    /* ---------- Wire the button and label ---------- */
    function setConvertButtonState(card, state) {
        const btn = card.querySelector('[data-convert-to-subs]');
        const label = btn?.querySelector('[data-convert-label]');
        if (!btn || !label) return;

        if (!state || !state.planId || state.saveCents <= 0) {
            btn.classList.add('hidden');
            btn.setAttribute('aria-busy', 'false');
            btn.dataset.planId = '';
            btn.dataset.subsLineCents = '';
            label.textContent = 'CONVERT TO SUBSCRIPTION';
            return;
        }

        btn.dataset.planId = String(state.planId);
        btn.dataset.subsLineCents = String(state.subsLineCents || 0);
        label.textContent = `CONVERT TO SUBSCRIPTION SAVE ${formatMoneyFromCents(state.saveCents)}`;
        btn.classList.remove('hidden');
        btn.setAttribute('aria-busy', 'false');
    }


    function hydrateConvertButtons() {
        const $ = (s, r = document) => r.querySelector(s);
        const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

        // --- helpers used only here (keep self-contained) ---
        function canonicalLabel(n, unit) {
            const u = String(unit).toLowerCase();
            if (u.startsWith('day')) return `${n} ${n === 1 ? 'Day' : 'Days'}`;
            if (u.startsWith('week')) return `${n} ${n === 1 ? 'Week' : 'Weeks'}`;
            if (u.startsWith('month')) return `${n} ${n === 1 ? 'Month' : 'Months'}`;
            return `${n} ${unit}`;
        }
        function labelToToken(label) {
            const m = String(label || '').match(/(\d+)\s*(day|days|week|weeks|month|months)/i);
            if (!m) return String(label || '').trim().toLowerCase();
            const n = Number(m[1]); const u = m[2].toLowerCase();
            return u.startsWith('day') ? `${n}d` : u.startsWith('week') ? `${n}w` : `${n}m`;
        }
        function planToToken(plan) {
            const unit = plan?.delivery_policy?.interval || plan?.billing_policy?.interval || '';
            const count = plan?.delivery_policy?.interval_count || plan?.billing_policy?.interval_count || '';
            if (unit && count) return labelToToken(canonicalLabel(Number(count), String(unit)));
            const name = String(plan?.name || '');
            return labelToToken(name);
        }
        function setPref(variantId, token) { // your LS pref map used by the selector
            const k = 'lrc-subs-frequency-pref';
            let m = {};
            try { m = JSON.parse(localStorage.getItem(k) || '{}') || {}; } catch { }
            m[String(variantId)] = token || '';
            try { localStorage.setItem(k, JSON.stringify(m)); } catch { }
        }
        function formatMoneyFromCents(c) {
            const n = Math.max(0, Math.round(Number(c) || 0));
            if (window.Shopify?.formatMoney) { try { return Shopify.formatMoney(n, window.theme?.moneyFormat || '${{amount}}'); } catch { } }
            return (n / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify?.currency?.active || 'USD') });
        }

        // --- UI writer for button ---
        function setConvertButtonState(card, state) {
            const btn = card.querySelector('[data-convert-to-subs]');
            const label = btn?.querySelector('[data-convert-label]');
            if (!btn || !label) return;

            if (!state || !state.planId || state.saveCents <= 0) {
                btn.classList.add('hidden');
                btn.dataset.planId = '';
                btn.dataset.planToken = '';
                label.textContent = 'CONVERT TO SUBSCRIPTION';
                return;
            }
            btn.dataset.planId = String(state.planId);
            btn.dataset.planToken = String(state.planToken || '');
            label.textContent = `CONVERT TO SUBSCRIPTION SAVE ${formatMoneyFromCents(state.saveCents)}`;
            btn.classList.remove('hidden');
        }

        // --- main hydrate ---
        (async () => {
            const { mode, tiers } = getOneTimeRewardsConfig();
            const cart = await getCart();
            const baseline = computeSubsBaseline(cart, mode); // qty or cents

            $$('.lrc-one-time-item-card').forEach(card => {
                const qty = Number(card.getAttribute('data-qty') || 1);
                const unitCents = Number(card.getAttribute('data-unit-price-cents') || 0);
                const storeMetric = (mode === 'quantity-based')
                    ? (baseline + qty)
                    : (baseline + unitCents * qty);

                // find unlocked tier → group name
                const unlocked = findUnlockedTier(tiers, storeMetric);
                const groupName = unlocked?.planName || null;

                // parse groups on this product
                const groups = parseGroups(card.getAttribute('data-selling-plan-groups'));
                if (!groups.length || !groupName) {
                    setConvertButtonState(card, { saveCents: 0, planId: null });
                    return;
                }

                // find the group by name (fallback to first group)
                const norm = s => String(s || '').trim().toLowerCase();
                const group = groups.find(g => norm(g?.name) === norm(groupName)) || groups[0];
                const plans = Array.isArray(group?.selling_plans) ? group.selling_plans : [];
                if (!plans.length) {
                    setConvertButtonState(card, { saveCents: 0, planId: null });
                    return;
                }

                // >>> IMPORTANT: choose the FIRST frequency in the group (your requirement)
                const chosenPlan = plans[0];

                // compute savings using FIRST plan’s adjustment
                const adj = Array.isArray(chosenPlan.price_adjustments) ? chosenPlan.price_adjustments[0] : null;
                const perUnitSave = computeSaveCentsFromAdjustmentLocal(adj, unitCents);
                const saveCents = Math.max(0, perUnitSave * qty);

                // set button label/state
                setConvertButtonState(card, {
                    saveCents,
                    planId: chosenPlan.id,
                    planToken: planToToken(chosenPlan)
                });

                // wire click → remove one-time, add with chosenPlan (FIRST), update LS pref so selector shows it
                const btn = card.querySelector('[data-convert-to-subs]');
                if (!btn) return;

                btn.onclick = async () => {
                    const lineKey = card.getAttribute('data-line-key');
                    const variantId = Number(card.getAttribute('data-variant-id'));
                    const qtyNow = Number(card.getAttribute('data-qty') || 1);
                    const planId = Number(btn.dataset.planId);
                    const planToken = String(btn.dataset.planToken || '');

                    const spinner = btn.querySelector('[data-spinner]');
                    btn.setAttribute('aria-busy', 'true');
                    spinner?.classList.remove('hidden');

                    try {
                        // remove the one-time line
                        if (lineKey) {
                            const res1 = await fetch('/cart/change.js', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                                body: JSON.stringify({ id: lineKey, quantity: 0 })
                            });
                            const json1 = await res1.json();
                            console.log('[Cart CHANGE API] (remove one-time)', { id: lineKey, quantity: 0 }, json1);
                        }

                        // add as subscription with the FIRST plan
                        const payload2 = { items: [{ id: variantId, quantity: qtyNow, selling_plan: planId }] };
                        const res2 = await fetch('/cart/add.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                            body: JSON.stringify(payload2)
                        });
                        const json2 = await res2.json();
                        console.log('[Cart ADD API] (convert to subs, first plan)', payload2, json2);

                        // >>> Make the frequency selector show this FIRST plan immediately on hydrate:
                        setPref(variantId, planToken); // selector reads this and will preselect it

                        // keep cache fresh & redraw
                        try {
                            const c = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
                            window.lrcWriteCartCache?.(c);
                        } catch { }
                        await window.lrcUpdateCartDrawer?.({});

                    } catch (e) {
                        console.warn('[LRC] convert to subs failed', e);
                        alert('Could not convert to subscription. Please try again.');
                    } finally {
                        spinner?.classList.add('hidden');
                        btn.setAttribute('aria-busy', 'false');
                    }
                };
            });
        })();
    }


    // Keep card data-qty in sync when +/- clicks happen
    function syncCardQtyFromDOM(card) {
        const qEl = card.querySelector('[data-qty-value]');
        if (!qEl) return;
        const q = parseInt(qEl.textContent || qEl.value || '1', 10) || 1;
        card.setAttribute('data-qty', String(q));
    }
    function primeQtySync() {
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-change-qty]');
            if (!btn) return;
            const card = e.target.closest('.lrc-one-time-item-card');
            if (!card) return;
            // Let your existing change handler run; after drawer updates, hydration will rerun.
            // We still optimistically sync the attribute for smoother precompute (optional).
            setTimeout(() => syncCardQtyFromDOM(card), 0);
        });
    }

    document.addEventListener('DOMContentLoaded', () => {
        primeQtySync();
        hydrateConvertButtons();
    });
    document.addEventListener('lrc:drawer-updated', () => {
        hydrateConvertButtons();
    });
})();


// ===== Sync ALL subscription items' selected plans on checkout click (sequential, refetch after each) =====
// (function () {
//     'use strict';
//     if (window.__lrcCheckoutPlanSyncWiredV2) return;
//     window.__lrcCheckoutPlanSyncWiredV2 = true;

//     const CHECKOUT_SEL = '[data-lrc-checkout-open], button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href$="/checkout"]';

//     const isEl = (x) => !!(x && x.nodeType === 1 && typeof x.removeAttribute === 'function');

//     async function getCart() {
//         const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
//         const cart = await r.json();
//         try { localStorage.setItem('lrc-custom-get-cart', JSON.stringify({ t: Date.now(), cart })); } catch { }
//         return cart;
//     }

//     async function guardedChange(payload) {
//         // Always use /cart/change.js with retries; payload can be { id: key, quantity, selling_plan }
//         let tries = 3, attempt = 0;
//         while (attempt < tries) {
//             try {
//                 const r = await fetch('/cart/change.js', {
//                     method: 'POST', credentials: 'same-origin',
//                     headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
//                     body: JSON.stringify(payload)
//                 });
//                 if (r.ok) return await r.json().catch(() => ({}));
//                 if (r.status === 429 || r.status >= 500) { attempt++; await new Promise(s => setTimeout(s, 300 * attempt)); continue; }
//                 // Non-retryable
//                 try { return await r.json(); } catch { return {}; }
//             } catch (e) {
//                 attempt++;
//                 if (attempt >= tries) throw e;
//                 await new Promise(s => setTimeout(s, 300 * attempt));
//             }
//         }
//     }

//     // Build a list from the UI: one entry per rendered subscription card
//     function collectDesiredEntriesFromUI() {
//         const out = []; // [{variantId, lineKey?, selectedPlanId|null}]
//         document.querySelectorAll('.lrc-subscription-item-card').forEach(card => {
//             const variantId = Number(card.getAttribute('data-lrc-variant-id'));
//             if (!Number.isFinite(variantId)) return;
//             const sel = card.querySelector('[data-subs-frequency-select]');
//             if (!sel) return;
//             const val = sel.value;
//             const selectedPlanId = (val && val !== 'one-time') ? Number(val) : null; // null = one-time
//             const lineKey = card.getAttribute('data-line-key') || card.getAttribute('data-lrc-line-key') || ''; // use if available
//             out.push({ variantId, lineKey, selectedPlanId });
//         });
//         console.log('[LRC][CheckoutSync] UI entries:', out);
//         return out;
//     }

//     // Find the current cart line for an entry. Prefer key, else first subs line for that variant.
//     function findCartLineForEntry(cart, entry) {
//         const items = Array.isArray(cart?.items) ? cart.items : [];
//         if (entry.lineKey) {
//             const byKey = items.find(li => li.key === entry.lineKey);
//             if (byKey) return byKey;
//         }
//         // fallback: first subscription line with this variant
//         return items.find(li => Number(li.variant_id) === Number(entry.variantId) && !!li.selling_plan_allocation) || null;
//     }

//     async function syncAllPlansOnce() {
//         const entries = collectDesiredEntriesFromUI();

//         // Mirror into your pending LS so other flows stay aligned
//         try {
//             const k = 'lrc-custom-pending-cart-ops';
//             const p = JSON.parse(localStorage.getItem(k) || '{}');
//             p.planByVariantId = p.planByVariantId || {};
//             entries.forEach(e => {
//                 if (e.selectedPlanId != null) {
//                     p.planByVariantId[String(e.variantId)] = e.selectedPlanId;
//                 } else {
//                     // If switching to one-time, remove pending mapping so it doesn't re-apply later
//                     delete p.planByVariantId[String(e.variantId)];
//                 }
//             });
//             localStorage.setItem(k, JSON.stringify(p));
//         } catch { }

//         // Nothing to do
//         if (!entries.length) {
//             console.log('[LRC][CheckoutSync] No subscription entries found in UI.');
//             return;
//         }

//         // IMPORTANT: Process sequentially and refetch cart after EACH change
//         let cart = await getCart();

//         for (let idx = 0; idx < entries.length; idx++) {
//             const entry = entries[idx];

//             // Re-locate the line in the current cart snapshot
//             const line = findCartLineForEntry(cart, entry);
//             if (!line) {
//                 console.log('[LRC][CheckoutSync] Skip (no matching subs line in cart):', entry);
//                 continue;
//             }

//             const currentPlanId = line.selling_plan_allocation?.selling_plan?.id || null;
//             const qty = Number(line.quantity) || 1;

//             // Compute desired payload
//             const payload = {
//                 id: line.key,
//                 quantity: qty,
//                 selling_plan: (entry.selectedPlanId == null ? null : Number(entry.selectedPlanId))
//             };

//             // Skip exact no-ops (same plan/null)
//             if ((currentPlanId == null && payload.selling_plan == null) ||
//                 (currentPlanId != null && String(currentPlanId) === String(payload.selling_plan))) {
//                 console.log('[LRC][CheckoutSync] No change needed for line:', { variantId: entry.variantId, lineKey: line.key, currentPlanId });
//                 continue;
//             }

//             console.log(`[LRC][CheckoutSync] (${idx + 1}/${entries.length}) Applying:`, payload);

//             try {
//                 const result = await guardedChange(payload);
//                 console.log('[LRC][CheckoutSync] Result:', result);
//             } catch (e) {
//                 console.warn('[LRC][CheckoutSync] Change failed (continuing):', e);
//             }

//             // REFRESH CART to get new keys/indices before the next update
//             try { cart = await getCart(); } catch { }
//         }
//     }

//     async function onCheckoutClick(ev, trigger) {
//         ev.preventDefault();
//         // ev.stopPropagation();

//         const el = isEl(trigger) ? trigger : null;
//         if (el && el.__lrcSyncingPlans) return;
//         if (el) { el.__lrcSyncingPlans = true; el.setAttribute('aria-busy', 'true'); el.style.pointerEvents = 'none'; }

//         console.log('[LRC][CheckoutSync] Checkout clicked → syncing ALL subscription lines…');

//         try {
//             await syncAllPlansOnce();
//         } catch (e) {
//             console.warn('[LRC][CheckoutSync] Sync error (proceeding anyway):', e);
//         } finally {
//             if (isEl(el)) {
//                 el.removeAttribute('aria-busy');
//                 el.style.pointerEvents = '';
//                 el.__lrcSyncingPlans = false;
//             }
//         }
//     }

//     // Delegated click (survives re-renders)
//     document.addEventListener('click', (e) => {
//         const trigger = e.target && e.target.closest(CHECKOUT_SEL);
//         if (!trigger) return;
//         onCheckoutClick(e, trigger);
//     });
// })();


// ===== Sync ALL subscription items' selected plans on checkout click (sequential, refetch after each) =====
(function () {
    'use strict';
    if (window.__lrcCheckoutPlanSyncWiredV3) return;
    window.__lrcCheckoutPlanSyncWiredV3 = true;

    /* -------------------- Status & API (no timeouts) -------------------- */
    const STATUS_KEY = 'lrc-checkout-plan-sync-status';
    function readStatus() {
        try { return JSON.parse(localStorage.getItem(STATUS_KEY) || '{}') || {}; } catch { return {}; }
    }
    function writeStatus(s) {
        try { localStorage.setItem(STATUS_KEY, JSON.stringify(s || {})); } catch { }
    }
    function startRun() {
        const runId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const s = { runId, state: 'running', startedAt: Date.now(), finishedAt: null, success: null, changes: [], error: null };
        writeStatus(s);
        return s;
    }
    function appendChange(rec) {
        const s = readStatus();
        s.changes = Array.isArray(s.changes) ? s.changes : [];
        s.changes.push(rec);
        writeStatus(s);
    }
    function completeStatus(success, errObj) {
        const s = readStatus();
        s.state = 'done';
        s.success = !!success;
        s.error = success ? null : (errObj ? (errObj.message || String(errObj)) : 'unknown');
        s.finishedAt = Date.now();
        writeStatus(s);
        document.dispatchEvent(new CustomEvent('lrc:plan-sync-complete', { detail: s }));
        return s;
    }

    // Public helpers for other files (e.g., your 2nd checkout button)
    window.lrcPlanSync = window.lrcPlanSync || {};
    window.lrcPlanSync.getStatus = () => readStatus();
    window.lrcPlanSync.waitForCompleteStrict = () => new Promise((resolve) => {
        const s = readStatus();
        if (s && (s.state === 'done' || s.state === 'error')) return resolve(s);
        const onDone = (e) => { document.removeEventListener('lrc:plan-sync-complete', onDone); resolve(e?.detail || readStatus()); };
        document.addEventListener('lrc:plan-sync-complete', onDone);
    });

    /* -------------------- DOM & HTTP helpers -------------------- */
    const CHECKOUT_SEL = '[data-lrc-checkout-open], button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href$="/checkout"]';
    const isEl = (x) => !!(x && x.nodeType === 1 && typeof x.removeAttribute === 'function');

    async function getCart() {
        const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
        const cart = await r.json();
        try { localStorage.setItem('lrc-custom-get-cart', JSON.stringify({ t: Date.now(), cart })); } catch { }
        return cart;
    }

    async function guardedChange(payload) {
        // payload: { id, quantity, selling_plan|null }
        let tries = 3, attempt = 0;
        while (attempt < tries) {
            try {
                const r = await fetch('/cart/change.js', {
                    method: 'POST', credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify(payload)
                });
                const json = await r.json().catch(() => ({}));
                if (r.ok) return json;
                if (r.status === 429 || r.status >= 500) { attempt++; await new Promise(s => setTimeout(s, 300 * attempt)); continue; }
                return json; // non-retryable error, still return body for logging
            } catch (e) {
                attempt++;
                if (attempt >= tries) throw e;
                await new Promise(s => setTimeout(s, 300 * attempt));
            }
        }
    }

    function collectDesiredEntriesFromUI() {
        const out = []; // [{variantId, lineKey?, selectedPlanId|null}]
        document.querySelectorAll('.lrc-subscription-item-card').forEach(card => {
            const variantId = Number(card.getAttribute('data-lrc-variant-id'));
            if (!Number.isFinite(variantId)) return;
            const sel = card.querySelector('[data-subs-frequency-select]');
            if (!sel) return;
            const val = sel.value;
            const selectedPlanId = (val && val !== 'one-time') ? Number(val) : null; // null = one-time
            const lineKey = card.getAttribute('data-line-key') || card.getAttribute('data-lrc-line-key') || '';
            out.push({ variantId, lineKey, selectedPlanId });
        });
        return out;
    }

    function findCartLineForEntry(cart, entry) {
        const items = Array.isArray(cart?.items) ? cart.items : [];
        if (entry.lineKey) {
            const byKey = items.find(li => li.key === entry.lineKey);
            if (byKey) return byKey;
        }
        return items.find(li => Number(li.variant_id) === Number(entry.variantId) && !!li.selling_plan_allocation) || null;
    }

    /* -------------------- Core sync (sequential) -------------------- */
    async function syncAllPlansOnce() {
        const entries = collectDesiredEntriesFromUI();

        // Mirror into pending LS (keeps other flows aligned)
        try {
            const k = 'lrc-custom-pending-cart-ops';
            const p = JSON.parse(localStorage.getItem(k) || '{}') || {};
            p.planByVariantId = p.planByVariantId || {};
            entries.forEach(e => {
                if (e.selectedPlanId != null) p.planByVariantId[String(e.variantId)] = e.selectedPlanId;
                else delete p.planByVariantId[String(e.variantId)];
            });
            localStorage.setItem(k, JSON.stringify(p));
        } catch { }

        if (!entries.length) return;

        let cart = await getCart();

        for (let idx = 0; idx < entries.length; idx++) {
            const entry = entries[idx];

            const line = findCartLineForEntry(cart, entry);
            if (!line) {
                appendChange({ step: idx + 1, variantId: entry.variantId, skipped: true, reason: 'no-matching-line' });
                continue;
            }

            const currentPlanId = line.selling_plan_allocation?.selling_plan?.id || null;
            const qty = Number(line.quantity) || 1;

            const payload = { id: line.key, quantity: qty, selling_plan: (entry.selectedPlanId == null ? null : Number(entry.selectedPlanId)) };

            // Skip exact no-ops
            if ((currentPlanId == null && payload.selling_plan == null) ||
                (currentPlanId != null && String(currentPlanId) === String(payload.selling_plan))) {
                appendChange({ step: idx + 1, variantId: entry.variantId, lineKey: line.key, noOp: true, currentPlanId });
                continue;
            }

            try {
                const result = await guardedChange(payload);
                //console.log('[LRC][CheckoutSync] Result:', result);
                appendChange({ step: idx + 1, variantId: entry.variantId, lineKey: line.key, payload, result });
            } catch (e) {
                appendChange({ step: idx + 1, variantId: entry.variantId, lineKey: line.key, payload, error: (e?.message || String(e)) });
            }

            // refetch cart for next iteration (new keys/indices)
            try { cart = await getCart(); } catch { }
        }
    }

    /* -------------------- Click handler -------------------- */
    async function onCheckoutClick(ev, trigger) {
        ev.preventDefault();

        const el = isEl(trigger) ? trigger : null;
        if (el && el.__lrcSyncingPlans) return;

        if (el) { el.__lrcSyncingPlans = true; el.setAttribute('aria-busy', 'true'); el.style.pointerEvents = 'none'; }

        // start run + mark running
        startRun();

        try {
            await syncAllPlansOnce();
            completeStatus(true);
        } catch (e) {
            console.warn('[LRC][CheckoutSync] Fatal sync error:', e);
            completeStatus(false, e);
        } finally {
            if (isEl(el)) {
                el.removeAttribute('aria-busy');
                el.style.pointerEvents = '';
                el.__lrcSyncingPlans = false;
            }
        }
    }

    // Delegated click (survives re-renders)
    document.addEventListener('click', (e) => {
        const trigger = e.target && e.target.closest(CHECKOUT_SEL);
        if (!trigger) return;
        onCheckoutClick(e, trigger);
    });
})();


/* ---------- One-time items: show compare-at price when discounted ---------- */
(function () {
    // local money helper
    function formatMoney(c) {
        const n = Math.max(0, Math.round(Number(c) || 0));
        try {
            if (window.Shopify?.formatMoney) return Shopify.formatMoney(n, window.theme?.moneyFormat || '${{amount}}');
        } catch { }
        return (n / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify?.currency?.active || 'USD') });
    }

    // cart line helpers
    function isGiftLine(li) {
        return ((li?.product_type || '').toLowerCase() === 'gift') ||
            (li?.properties && li.properties['_lrcGift'] === '1');
    }
    function isSubsLine(li) { return !!li?.selling_plan_allocation; }
    function hasLineDiscount(li) {
        return Array.isArray(li?.line_level_discount_allocations) &&
            li.line_level_discount_allocations.length > 0;
    }

    // If one-time line has discount → compare = unit price * qty; else 0 (hide)
    function computeOneTimeCompareAtCents(li) {
        if (!li || isGiftLine(li) || isSubsLine(li)) return 0;
        if (hasLineDiscount(li) || li?.total_discount > 0){
            const qty = Math.max(1, Number(li.quantity) || 1);
            const unit = Math.max(0, Math.round(Number(li.price) || 0)); // unit cents
            return unit * qty;
        }else{
            return 0;
        }
    }

    function refreshOneTimeCompareAtPrices(cart) {
        if (!cart) return;
        const oneTimeLines = (cart.items || []).filter(li => !isGiftLine(li) && !isSubsLine(li));
        const cards = Array.from(document.querySelectorAll('.lrc-one-time-item-card'));

        cards.forEach((card, idx) => {
            const line = oneTimeLines[idx];
            if (!line) return;

            const compareEl = card.querySelector('[data-compare-price], .lrc-compare-price-one-time');
            if (!compareEl) return;

            const compareCents = computeOneTimeCompareAtCents(line);
            if (compareCents > 0) {
                compareEl.textContent = formatMoney(compareCents);
                compareEl.classList.remove('hidden');
                // optionally add strike styling:
                // compareEl.classList.add('line-through', 'text-neutral-400');
            } else {
                compareEl.textContent = '';
                compareEl.classList.add('hidden');
                // compareEl.classList.remove('line-through', 'text-neutral-400');
            }
        });
    }

    async function run() {
        try {
            const cart = await (window.lrcGetCartCachedOrFetch?.() ||
                fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json()));
            refreshOneTimeCompareAtPrices(cart);
        } catch { /* no-op */ }
    }

    // fire on initial load and whenever the drawer rehydrates
    document.addEventListener('DOMContentLoaded', run);
    document.addEventListener('lrc:drawer-updated', run);
})();